import { useEffect, useRef, useState, type ReactNode } from 'react';
import { openInSafari, type IosBrowser } from '@shared/pwa';
import { cn } from '@shared/utils';

export interface InstallPromptCardProps {
  /** app 名稱，會出現在「將 ⋯ 加入主畫面」裡 */
  appName: string;
  /** app 圖示。省略時只顯示文字 */
  icon?: ReactNode;
  /**
   * 這個平台能不能一鍵安裝。
   *
   * `true` 時放「安裝」按鈕（Chromium 系列給得起 `beforeinstallprompt`），
   * 其餘顯示該平台的手動步驟。**沒有事件卻放一顆按鈕，按下去什麼都不會發生
   * ——那比不放更糟。**
   */
  canPrompt: boolean;
  /** 手動步驟要照哪個平台寫 */
  platform: 'ios' | 'android';
  /** iOS 上是哪個瀏覽器——分享鍵的位置各家不同，見 `@shared/pwa` 的 `IosBrowser` */
  iosBrowser?: IosBrowser;
  onInstall: () => void;
  onDismiss: () => void;
  /**
   * 覆寫「在 Safari 開啟」的實作，只給 Storybook 與測試用。
   * 正式使用不需要傳——預設就是 `@shared/pwa` 的 `openInSafari`。
   */
  onOpenInSafari?: typeof openInSafari;
  /** 位置覆寫，見 InstallPrompt */
  className?: string;
}

/**
 * 卡片佔掉的底部空間，單位 px，掛在 `<html>` 上。
 *
 * 卡片是 `fixed` 的，**不佔版面高度**——置中的內容因此會以整個容器算中心，
 * 算出來的位置有一段被卡片蓋住，看起來就是整組往下掉。要讓開它的版面用
 * `pb-[var(--install-prompt-inset,0px)]` 之類的寫法即可，卡片消失時變數會被移除、
 * 自動回到 0。
 *
 * 值是「從視窗底部往上算」的總高度（卡片高 + 它自己的下緣間距），而不是單純的
 * 卡片高度：Android 版多一顆安裝按鈕、iOS 版文案會折行，實際高度各不相同，寫死
 * 一個數字補不準。
 */
const INSET_VAR = '--install-prompt-inset';

/**
 * 安裝提示的外觀。**不含任何平台偵測**——那些在 `InstallPrompt` 裡，這一支只負責
 * 長相，因此 Storybook 能直接把三種狀態並排出來看（桌機瀏覽器永遠不會觸發真的
 * 安裝事件，混在一起就沒辦法驗證了）。
 */
export function InstallPromptCard({
  appName,
  icon,
  canPrompt,
  platform,
  iosBrowser = 'safari',
  onInstall,
  onDismiss,
  onOpenInSafari = openInSafari,
  className,
}: InstallPromptCardProps) {
  /* 跳轉失敗後的替代說明。成功的話頁面已經切走，這個狀態不會有人看到 */
  const [fallbackHint, setFallbackHint] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  /*
   * 回報自己佔掉的底部空間（見 INSET_VAR）。
   *
   * 用 ResizeObserver 而非量一次就算了：文案折行、按鈕出現與否、字級設定都會改變
   * 高度，而那些變化不會經過 React 的渲染（例如使用者調系統字級）。
   */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const publish = () => {
      const inset = window.innerHeight - card.getBoundingClientRect().top;
      document.documentElement.style.setProperty(INSET_VAR, `${inset}px`);
    };

    const observer = new ResizeObserver(publish);
    observer.observe(card);
    window.addEventListener('resize', publish);
    publish();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      /* 卡片消失就把變數收掉，讓讓開它的版面自動回到 0 */
      document.documentElement.style.removeProperty(INSET_VAR);
    };
  }, []);

  /**
   * iOS 上非 Safari 的瀏覽器才需要這顆鍵。
   *
   * Chrome 與 Edge（iOS 17 起）自己就裝得起來，所以它是**次要**選項，擺在步驟說明
   * 底下——把「換一個瀏覽器」講成主要作法，會把裝得起來的人趕走。Firefox 這類沒有
   * 入口的瀏覽器則只剩這條路。
   */
  const handleOpenInSafari = async () => {
    const result = await onOpenInSafari();

    if (result === 'copied') {
      setFallbackHint('已複製連結，請在 Safari 貼上開啟。');
    } else if (result === 'failed') {
      setFallbackHint('請複製本頁網址，改用 Safari 開啟。');
    }
  };

  const showSafariButton = platform === 'ios' && iosBrowser !== 'safari';
  return (
    <div
      ref={cardRef}
      role="complementary"
      aria-label={`將${appName}加入主畫面`}
      className={cn(
        /*
         * 浮動卡片而非貼底橫幅：它可能需要讓開底部的元素（如 diet 的記錄鍵），
         * 四邊留白的卡片懸在任何高度都成立，滿版橫幅懸空則像沒對齊。
         */
        'animate-fade-in fixed inset-x-4 z-[1300]',
        'bottom-[calc(1rem+env(safe-area-inset-bottom))]',
        'rounded-2xl border border-border bg-card-solid p-4 shadow-lg',
        className,
      )}
    >
      <div className="mx-auto flex max-w-5xl items-start gap-3">
        {icon && (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-accent">
            {icon}
          </span>
        )}

        <div className="flex flex-1 flex-col gap-1">
          <p className="font-display text-sm text-strong">
            將{appName}加入主畫面
          </p>

          {canPrompt ? (
            <p className="text-xs leading-relaxed text-muted-foreground">
              離線也能用，開啟更快，也不會再跳出網址列。
            </p>
          ) : (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {platform === 'ios' ? IOS_STEPS[iosBrowser] : ANDROID_STEPS}
            </p>
          )}

          {/* 跳轉沒成功時的替代說明，取代不了步驟，只補在它下面 */}
          {fallbackHint && (
            <p className="text-xs leading-relaxed text-accent">
              {fallbackHint}
            </p>
          )}

          {canPrompt && (
            <button
              type="button"
              onClick={() => onInstall()}
              className={cn(
                'mt-2 min-h-11 self-start rounded-lg px-5',
                'bg-accent font-display text-sm text-strong text-accent-foreground',
                'transition-all duration-200 ease-out hover:brightness-110 active:scale-[0.98]',
                'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              )}
            >
              安裝
            </button>
          )}

          {/*
            次要按鈕：外框而非實心，視覺上不與「安裝」搶。iOS 沒有官方 API 能指定
            Safari 開啟，這顆鍵走的是非官方的 scheme，失敗會退回複製連結——
            所以它的文案是「在 Safari 開啟」而不是承諾一定跳得過去。
          */}
          {showSafariButton && (
            <button
              type="button"
              onClick={() => void handleOpenInSafari()}
              className={cn(
                'mt-2 min-h-11 self-start rounded-lg border border-border-control px-5',
                'font-display text-sm text-strong',
                'transition-all duration-200 ease-out hover:bg-muted active:scale-[0.98]',
                'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              )}
            >
              在 Safari 開啟
            </button>
          )}
        </div>

        {/* 44px 觸控目標：它緊鄰文字，小一點就會誤觸到卡片本身 */}
        <button
          type="button"
          onClick={() => onDismiss()}
          aria-label="關閉"
          className={cn(
            '-mt-1 -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
            'text-muted-foreground transition-colors hover:text-foreground',
          )}
        >
          <svg
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * iOS 的手動步驟。
 *
 * 引擎都是 WebKit，但**入口在哪裡由各家外殼決定**：Safari 的分享鍵在下方工具列，
 * Chrome 與 Edge（iOS 17 起）在網址列，Firefox 則沒有這個選項。
 */
const IOS_STEPS: Record<IosBrowser, string> = {
  safari: '點下方工具列的分享鍵，選「加入主畫面」。',
  chromium: '點網址列右側的分享鍵，選「加入主畫面」；找不到的話改用 Safari。',
  unsupported: '這個瀏覽器沒有加入主畫面的選項，請改用 Safari 開啟本頁。',
};

/** Android 但沒收到事件（多半是非 Chromium 的瀏覽器） */
const ANDROID_STEPS = '點右上角的選單，選「安裝應用程式」或「加入主畫面」。';
